import { FetchResult } from '@apollo/client';
import { Text } from '@shared/components';
import { CircularProgress } from '@shared/components/plugin-components';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { OriginatorDetails } from '@shared/features';
import {
  JobType,
  ProcessingStatus,
  UpdateOrderInput,
  UpdateOrderMutation,
  useOrderQuery,
  useUpdateOrderMutation,
} from '@shared/generated/graphql';

import { saveNewChatDraft } from '@shared/features/chats-page/components/ChatMessageInput';
import { getOriginatorOptions } from '@shared/features/originator-search/getInitialOriginatorOption';
import { orderFromFragment } from '@shared/graphql/fromFragments/order';
import useOrganizationSettings from '@shared/graphql/hooks/organization/useOrganizationSettings';
import useInitializeTemplate from '@shared/graphql/hooks/templates/useInitializeTemplate';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useSaveContactCue } from '@shared/hooks/useSaveContactCue';
import {
  ActionRegistryContextProvider,
  useActionRegistryContext,
} from '@shared/plugin/contexts/ActionRegistryContext';
import useOriginators from '@shared/plugin/hooks/useOriginators';
import { shouldShowReplicatePrompt } from '@shared/plugin/utils/shouldShowReplicatePrompt';
import { Order } from '@shared/types/order';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { hasChatsAccess } from '@shared/utils/permissions';
import { CHATS_ROUTE } from 'clerk_common/constants/urls/routes';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OriginatorOnboardingType } from '../OriginatorOnboarding/components/OriginatorOnboardingWizard/types';
import { useInitiateOriginatorOnboarding } from '../OriginatorOnboarding/hooks/useInitiateOriginatorOnboarding';
import { getOnboardingRoute } from '../OriginatorOnboarding/OriginatorOnboarding';
import { MultiOrderBanner } from './components/MultiOrderBanner';
import OrderReviewForm from './components/OrderReviewForm';
import { useReprocessOrder } from './hooks/useReprocessOrder';
import OrderSidePanelHeader from './OrderSidePanelHeader';

const POLLING_INTERVAL = 10 * 1000;

const buildConfigureCustomerMessage = (customerName: string) =>
  `I want to update the load building instructions for ${customerName} to `;

const ROOT = makeRootClassName('OrderSidePanel');
export const el = makeElementClassNameFactory(ROOT);

type OriginitorOption = {
  id: string;
  label: string;
  details: string;
};
interface OrderSidePanelProps {
  order: Order;
  originatorOptions: OriginitorOption[];
  loading: boolean;
  updateOriginator: (
    args: OriginatorIdentifiers
  ) => Promise<void | FetchResult<UpdateOrderMutation>>;
  onChangeOriginator: () => void;
  handleOriginatorSearch: (query?: string) => void;
  webBaseUrl: string;
  orderEvents: Order['orderEvents'];
  disableNewCustomerCreation?: boolean;
}

const OrderSidePanel = ({
  order,
  originatorOptions,
  loading,
  updateOriginator,
  onChangeOriginator,
  handleOriginatorSearch,
  webBaseUrl,
  orderEvents,
  disableNewCustomerCreation,
}: OrderSidePanelProps) => {
  const [showReplicatePrompt, setShowReplicatePrompt] = useState(
    shouldShowReplicatePrompt(order)
  );
  const { handleReplicate } = useActionRegistryContext();
  const { sendToast } = useToast();
  const { initiateOriginatorOnboarding } = useInitiateOriginatorOnboarding({
    orderId: order.id,
    organizationId: order.organization.id,
  });
  const navigate = useNavigate();
  const { initializeTemplate, originatorHasTemplate } = useInitializeTemplate({
    originatorId: order.originator.id,
  });
  const { defaultOrgId, hasVee, isVoomaAdmin } = useMeContext();

  const sendErrorToast = (e: Error) => {
    sendToast('Something went wrong', {
      description: e.message,
      variant: 'error',
      isDismissible: true,
    });
  };

  const handleBuildTheRest = async (orderId: string) => {
    await handleReplicate(orderId).then(() => {
      setShowReplicatePrompt(false);
    });
  };

  const handleChangeOriginator = async (id: string) => {
    await updateOriginator({ originatorId: id }).catch((e) =>
      sendErrorToast(e)
    );
    onChangeOriginator();
  };

  const openCustomerQuestionnaire = async (originatorId: string) => {
    if (!originatorHasTemplate) {
      await initializeTemplate();
    }
    navigate(
      getOnboardingRoute(
        OriginatorOnboardingType.EXISTING_ORIGINATOR,
        order.id,
        originatorId
      )
    );
  };

  const handleConfigureCustomer = async () => {
    const { id, name } = order.originator;
    if (!id) return;

    if (defaultOrgId && name && hasChatsAccess({ hasVee, isVoomaAdmin })) {
      saveNewChatDraft(defaultOrgId, buildConfigureCustomerMessage(name));
      navigate(CHATS_ROUTE);
      return;
    }
    await openCustomerQuestionnaire(id);
  };

  const handleChangeTransmitId = async (id: string) => {
    await updateOriginator({ transmitId: id }).catch((e) => sendErrorToast(e));
  };

  // TODO(jacob): Figure out what to do if there are multiple shipments
  const shipmentId = order.shipmentIds.at(0);

  return (
    <div className={el`order-container`}>
      <div className={el`order-header`}>
        <OrderSidePanelHeader
          webBaseUrl={webBaseUrl}
          orderEvents={orderEvents}
          orderId={order?.id}
          shipmentId={shipmentId}
        />
        {showReplicatePrompt && (
          <MultiOrderBanner buildTheRest={() => handleBuildTheRest(order.id)} />
        )}
        <OriginatorDetails
          originator={order.originator}
          originatorOptions={originatorOptions}
          updateOriginator={handleChangeOriginator}
          updateTransmitId={handleChangeTransmitId}
          loading={loading}
          transmitId={order.transmitId}
          customerDetailsCTA={'Configure customer'}
          navigateToOriginatorDetails={handleConfigureCustomer}
          initiateOriginatorOnboarding={initiateOriginatorOnboarding}
          hideCreateOption={disableNewCustomerCreation}
          handleSearch={handleOriginatorSearch}
        />
      </div>
      <div className={el`order-details`}>
        {order.status === ProcessingStatus.PROCESSING ? (
          <div className="flex h-48 w-full flex-col items-center justify-center gap-4">
            <Text className="text-gray-500" type="body-sm">
              Order is being built...
            </Text>
            <CircularProgress />
          </div>
        ) : (
          <OrderReviewForm order={order} isLoading={loading} />
        )}
      </div>
    </div>
  );
};

type OriginatorIdentifiers = Pick<
  UpdateOrderInput,
  'originatorId' | 'transmitId'
>;
type OrderSidePanelContainerProps = {
  webBaseUrl: string;
};
const OrderSidePanelContainer = ({
  webBaseUrl,
}: OrderSidePanelContainerProps) => {
  const { page } = useAnalytics();
  const { orderId } = useParams();
  // TODO(parlato): useOrderQuery
  const {
    data,
    loading: loadingOrder,
    stopPolling,
    startPolling,
  } = useOrderQuery({
    variables: {
      id: orderId ?? '',
    },
    pollInterval: POLLING_INTERVAL,
    skip: !orderId,
  });
  const organizationId = data?.orderById?.organization?.id;
  const [originatorsSearchTerm, setOriginatorsSearchTerm] = useState<string>();
  const { settings } = useOrganizationSettings({
    id: organizationId ?? '',
    skip: !organizationId,
  });
  const { originators, isLoading: loadingOriginators } = useOriginators({
    organizationId,
    searchTerm: originatorsSearchTerm,
  });
  const originatorOptions = getOriginatorOptions(originators);

  const handleOriginatorSearch = (query?: string) => {
    setOriginatorsSearchTerm(query);
  };

  const [updateOrder] = useUpdateOrderMutation();

  const { promptToReprocess, loading: loadingReprocess } =
    useReprocessOrder(orderId);

  const { sendToast } = useToast();
  const { handleSendCue } = useSaveContactCue();

  const sendErrorToast = (e: Error) => {
    sendToast('Something went wrong', {
      description: e.message,
      variant: 'error',
      isDismissible: true,
    });
  };

  useEffect(() => {
    if (orderId) {
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [orderId]);

  const order = data?.orderById ? orderFromFragment(data?.orderById) : null;

  const updateOriginator = async ({
    originatorId,
    transmitId,
  }: OriginatorIdentifiers) => {
    const id = data?.orderById?.id;
    if (!id) return;
    const res = await updateOrder({
      variables: { input: { id, originatorId, transmitId } },
    }).catch((e) => sendErrorToast(e));

    const previousOriginatorId = data?.orderById?.originator?.id;
    if (!previousOriginatorId && !isNil(originatorId)) {
      // NOTE(max): We intentionally want to let this run async.
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleSendCue({ job: order?.job, originatorId, jobType: JobType.ORDERS });
    }

    return res;
  };

  const onChangeOriginator = () => {
    if (!orderId) return;
    promptToReprocess();
  };

  useEffect(() => {
    if (order?.id) {
      page('Order', { orderId: order?.id });
    }
  }, [order?.id]);

  const loading = loadingOrder || loadingOriginators;

  return (
    <ActionRegistryContextProvider>
      <>
        {loading && !order && (
          <div className="flex h-1/3 w-full items-center justify-center">
            <CircularProgress size="md" />
          </div>
        )}
        {!loading && !order && <div className="p-2">Could not find order</div>}
        {order && (
          <OrderSidePanel
            order={order as Order}
            originatorOptions={originatorOptions}
            updateOriginator={updateOriginator}
            loading={loading || loadingReprocess}
            onChangeOriginator={onChangeOriginator}
            handleOriginatorSearch={handleOriginatorSearch}
            webBaseUrl={webBaseUrl}
            orderEvents={data?.orderById.orderEvents || []}
            disableNewCustomerCreation={
              settings?.accessPolicies?.disableNewCustomerCreation ?? false
            }
          />
        )}
      </>
    </ActionRegistryContextProvider>
  );
};

export default OrderSidePanelContainer;
